/**
 * XssSafeWorker is a Worker API wrapper. This module assumes you know the Web
 * Worker API. Instead of calling `new Worker` you would call `new XssWorker`.
 * This will provide you with the same API you're used to with
 * `worker.postMessage` and `worker.onmessage`. Behind the scenes, if you're on
 * the same domain as the worker, it simply returns the native `new Worker`
 * return.
 *
 * However, if you're on a remote server and call this it will return what looks
 * like a Worker but isn't. When you call `worker.postMessage` on a remote
 * domain it is actually sending the message to a hidden iframe on the same
 * domain. That iframe contains the JS from the path you provided.
 *
 * 2 Step Setup:
 * 1. Replace your `new Worker` calls to `new XssWorker`
 *
 * 2. If you need to call postMessage from *within the worker* you will need to
 * make sure that you postMessage to the right place. Since this is using an
 * iframe under the hood you need to call `window.top.postMessage` unlike
 * `postMessage` or `this.postMessage.`. The module mixes in a `XssSafeWorker`
 * property that is set to `true` if you're in an iframe as a helper to do this.
 * Example code you might have is this inside the onmessage code in your worker:
 *
 *
 *     var postMessageTarget = data.XssSafeWorker ? window.top : this;
 *     postMessageTarget.postMessage({...});
 *
 *  It's important to note that this will not have the same performance as a
 *  worker since it will be single threaded. For small data sets this will
 *  actually be faster as a new thread doesn't need to spin up. However, for
 *  large data sets this will be much slower.
 *#
 * @param {string} path The path to the worker file
 */
(function (window) {
  function XssSafeWorker(path) {
    if (!path) {
      throw new Error("The path argument is required");
    }
    // We're going to try generate a Worker. If it fails we'll check the error
    // code to see if it's a XSS issue.
    try {
      return new Worker(path);
    } catch (error) {
      // TODO: throw error if its not a code 18 error
      // If it's 18 that's a SecurityError of remote location. This means we want
      // to add our polyfill
      if (error.code === 18) {
        // Stores the window of the iframe where you can access it's document
        var iframeWindow;
        // Once the iframe is loaded and ready this is set to true
        var iframeLoaded = false;
        // If an `worker.onmessage` is set, this will become true before the first
        // `worker.postMessage` is set.
        var onMessageSet = false;
        // This stores all the `worker.postMessage`s data that were called before
        // the iframe was ready to get them.
        var queue = [];

        /**
         * Runs through the queue array of the postMessage datas that were sent
         * and calls postMessage
         */
        var runQueue = function () {
          queue.forEach(function (data) {
            this.postMessage(data);
          }, this);

          // Reset the queue back to empty since all the queue items have
          // processed. NOTE: if there is an error this won't get set!
          queue = [];
        }.bind(this);

        // Generate the hidden iframe. Use Object.assign to mix in HTML props
        // without needing to do a bunch of iframeSandbox.someProp = ... etc
        var iframeSandbox = Object.assign(document.createElement("iframe"), {
          src: "", // intentionally left blank
          frameBorder: 0,
          scrolling: "no",
          style: "display:none",
        });

        // Add this to the parent document
        document.body.appendChild(iframeSandbox);

        // Add a load event listener that will backfill the postMessages that
        // couldn't run before
        iframeSandbox.addEventListener("load", function () {
          iframeLoaded = true;
          runQueue();
        });

        iframeWindow = iframeSandbox.contentWindow;

        iframeWindow.document.open();
        iframeWindow.document.write('<script src="' + path + '"></script>');
        iframeWindow.document.close();

        this.postMessage = function (data) {
          // Mix this in so you can check if you're on a different domain or not
          // inside of your worker
          data.XssSafeWorker = true;

          // If an onmessage was provided set it to the window which is required
          // to get an iframe message. We only want to call this once for each
          // XssSafeWorker instance and we want to use addEventListener instead
          // of `window.onmessage = ` as to not overwrite any previous listeners
          if (this.onmessage && !onMessageSet) {
            onMessageSet = true;
            window.addEventListener("message", this.onmessage);
          }
          // If the iframe is loaded we can just call postMessage to the iframe
          if (iframeLoaded) {
            iframeWindow.postMessage(data);
          }
          // If it's not pop it into the queue to be processed later
          else {
            queue.push(data);
          }
        };

        return {
          /**
           * This provides the `worker.postMessage` syntax so it's compatible for
           * both iframes and Workers. It mixes in the `XssSafeWorker` prop to the
           * data provided as the first arg so you can check if the postMessage
           * came from the same domain or not.
           *
           * @param {object} data The data you want to send to the worker
           */
          postMessage: this.postMessage,
        };
      }
    }
  }

  window.XssSafeWorker = XssSafeWorker;
})(window);
